import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Sheet } from '../ui/Sheet'
import { StagePage, type StageChrome } from './StagePage'
import { Icon } from '../ui/Icon'
import { PixelPet } from '../identity/PixelPet'
import { enterSends, keepEnter } from '../ui/enterSends'
import { BffError, type HeldTab, type HeldTabsBrowser } from '../transport/relay'
import { postAgentSay } from '../transport/api'
import { ApiError } from '../transport/http'
import type { Everywhere } from '../agents/everywhere'
import { useVoiceRecorder } from '../voice/useVoiceRecorder'
import { transcribeBlob } from '../voice/transcribe'
import { insertAtCaret } from '../voice/insertAtCaret'
import { registerMicHotkey, MIC_HOTKEY_LABEL } from '../voice/micHotkey'
import { micGate, recordCapMs, reportVoiceError } from '../billing/voiceGate'
import { useRetakeMoments, useTabPicture } from './useTabPicture'
import { tabRef, useSheetShowing } from './sheetPictures'
import { tabsHeldBy, useHeldTabs, type Held } from './heldTabs'

/** The sheet is at most this wide (shell.css `.sheet.tab-shot`); the picture is asked for at its size… */
const SHEET_MAX_CSS_PX = 760
/** …and the page in More's stage at most this wide (shell.css `.tab-shot-page .sp-col`). */
const PAGE_MAX_CSS_PX = 1200
/**
 * …and never narrower than a desktop tab is drawn: on a 1x screen the sheet's own size was
 * the whole picture, and "full size" showed it 4 % larger (the review of 2026-10-14).
 */
const FULL_SIZE_PX = 1440

/** The page's address, when the browser sent one and it is a web address — never anything else in an href. */
function webAddress(u: unknown): string | null {
  return typeof u === 'string' && /^https?:\/\//i.test(u) ? u : null
}

/** What a refusal says, in the service's own words when it has them. */
export function said(e: unknown): string {
  if (e instanceof BffError && typeof e.body['message'] === 'string') return e.body['message'] as string
  if (e instanceof ApiError) {
    const body = e.body as { detail?: unknown; message?: unknown; error?: unknown } | undefined
    for (const v of [body?.detail, body?.message, body?.error]) if (typeof v === 'string' && v) return v
  }
  return String((e as Error)?.message ?? e)
}

/**
 * A TAB AN AGENT HOLDS, SEEN FROM THE PHONE — owner, 2026-10-14: 在 Chrome Extension Tab
 * 裡面, 當使用者點入某一個 Tab 可以截圖給使用者看, 就跳出來一個視窗, 然後看到那個截圖然後
 * 下面還有一個輸入文字的方塊, 使用者可以直接從那個輸入文字的方塊跟它的 Agent 直接溝通 …
 * 也可以使用語音 … 不需要使用者再去看他的 Chrome Tab.
 *
 * The picture is asked of the browser that holds the tab (extension 0.2.22+), taken in
 * the tab's own turn, as wide as this screen can show (`fit`, device pixels), and kept
 * nowhere but here. The box speaks to the agent the way the composer does — /agent/say,
 * so a busy agent HOLDS it rather than having it typed into whatever is on its screen — on
 * the machine the agent is on, with the tab named so an agent holding several knows which.
 *
 * AN AGENT HOLDING SEVERAL TABS — owner, 2026-10-15: 我加了第二個分頁. 但是目前只能看到第一個
 * … 在開啟分頁預覽彈出視窗, 上面顯示可以切換的 tab. Its tabs are a row at the top, the one
 * shown marked; a tap moves the sheet to that tab — its picture taken as it would be on
 * opening it, the box then speaking of that tab. Only with `onSwitch`: the host keeps which
 * tab is open, and its corner shows the one looked at last (AgentTabCorner).
 *
 * A SHEET OR A PAGE. From the corner of a session it is a sheet over that session. From
 * More it is a page of the stage, in the list's place — owner, 2026-10-22: 點擊任何一個項目,
 * 則會在右側主顯示匡裡面顯示 … 的內容 (非彈跳視窗), 顯示匡的左上角有返回頁面的按鈕 — the
 * same parts, Back where the sheet has ✕.
 */
export function TabShotSheet({
  browser,
  told,
  onClose,
  onSwitch,
  onGoToAgent,
  page,
}: {
  browser: HeldTabsBrowser
  /** The row as the browser last said it. */
  told: HeldTab
  onClose: () => void
  /** Another of the agent's tabs, chosen in the row at the top: the host opens that one instead. */
  onSwitch?: (h: Held) => void
  /**
   * The agent's face is a door to its session (owner, 2026-10-14: 放上 Agent 的頭像然後當
   * 使用者點擊 Agent 頭像的時候, 就可以直接跳到該 Agent 的 Session 裡面去).
   */
  onGoToAgent?: (a: { session: string; hash: string; device: string }) => void
  /** Drawn as a page of More's stage, Back at its top left, instead of a sheet. */
  page?: StageChrome
}) {
  const qc = useQueryClient()
  const name = told.title || `Tab ${told.tab}`
  const canSee = browser.shots === true
  // Fixed when it opens: a phone turned on its side would otherwise ask a second picture and
  // blank the first meanwhile (the review of 2026-10-15).
  const [fit] = useState(() =>
    Math.min(2400, Math.max(FULL_SIZE_PX, Math.round(Math.min(window.innerWidth, page ? PAGE_MAX_CSS_PX : SHEET_MAX_CSS_PX) * (window.devicePixelRatio || 1)))),
  )

  /*
   * WHICH AGENT, BY NAME, ON WHICH MACHINE. The browser names the agent by hash and
   * machine; /agent/say wants its session (and the mounted host, for an agent on a
   * remote). The rail's own list has both — the cloud's roster can fall back to a hash
   * prefix for an agent it could not name, and that is no session at all.
   */
  const roster = qc.getQueryData<Everywhere>(['agents', 'all'])
  const row = roster?.agents.find((a) => a.hash === told.agent.hash && (a.device_id ?? null) === told.agent.device)
  const session = row?.session ?? told.agent.session
  const host = row?.host ?? null

  // With the page's address, for "Open page" (owner, 2026-10-14: 加上一個連結的按鈕); taken again
  // when the agent finishes its turn and when the person comes back (useRetakeMoments).
  const { shot, takeAgain } = useTabPicture({ browserId: browser.browser_id, tab: told.tab, fit, url: true, enabled: canSee })
  useRetakeMoments({ device: told.agent.device, session, onMoment: takeAgain })
  // While open, the corner of the agent's session takes none of this tab; closed, it shows this one's last.
  useSheetShowing(tabRef(browser.browser_id, told.tab), { image: shot.data?.image, at: shot.dataUpdatedAt })

  const [draft, setDraft] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [big, setBig] = useState(false)
  const draftEl = useRef<HTMLTextAreaElement>(null)

  /*
   * THE AGENT'S TABS, A ROW AT THE TOP — by its hash on its machine, in every browser; read as
   * they change while the sheet is open (a person is watching, as in More). The tab shown is
   * in the row even when it has left the agent's hands, so the row never loses its mark.
   */
  const list = useHeldTabs({ live: !!onSwitch })
  const isThis = (h: Held) => h.browser.browser_id === browser.browser_id && h.told.tab === told.tab
  const theirs = onSwitch ? tabsHeldBy(list.data?.browsers ?? [], { hash: told.agent.hash, device_id: told.agent.device }) : []
  const strip = theirs.length === 0 || theirs.some(isThis) ? theirs : [{ browser, told }, ...theirs]
  const moveTo = (h: Held) => {
    if (!onSwitch || isThis(h)) return
    setBig(false)
    setNote(null)
    onSwitch(h)
  }
  // The marked tab in view, in a row too long for the sheet.
  const stripEl = useRef<HTMLDivElement>(null)
  useEffect(() => {
    stripEl.current?.querySelector<HTMLElement>('.tab-shot-tab.on')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [browser.browser_id, told.tab])

  const say = useMutation({
    mutationFn: (text: string) => postAgentSay(session, `[Chrome tab ${told.tab}] ${text}`, host, told.agent.device),
    onMutate: () => setNote(null),
    onSuccess: (r, text) => {
      if (r.ok === false || r.error) {
        setNote(r.detail || r.error || 'The agent did not take it')
        return
      }
      // What was sent leaves the box; a next instruction typed or dictated while this one
      // was on its way stays — alone, or the next Enter would send this one again with it.
      setDraft((d) => {
        const at = d.indexOf(text)
        return at >= 0 && !d.slice(0, at).trim() ? d.slice(at + text.length).trimStart() : d
      })
      // Held is accepted — it is not "seen", and the line must not say it was.
      setNote(r.queued ? `Waiting for ${session}: ${r.detail ?? 'it is busy, and gets this next'}` : `Sent to ${session}`)
    },
    onError: (e) => setNote(said(e)),
  })
  const send = () => {
    const text = draft.trim()
    if (!text || say.isPending) return
    say.mutate(text)
  }

  /* The same three parts the composer's microphone uses, pointed at this box. */
  const voice = useVoiceRecorder({
    onClip: async (blob, durationMs) => {
      try {
        const r = await transcribeBlob(blob, durationMs)
        if (!r.text.trim()) {
          setNote('No speech was detected.')
          return
        }
        // Into the box AS IT IS NOW: what was typed during the transcription stays.
        const el = draftEl.current
        const now = el?.value ?? draft
        const next = insertAtCaret(now, el?.selectionStart ?? now.length, el?.selectionEnd ?? now.length, r.text)
        setDraft(next.value)
        requestAnimationFrame(() => {
          const box = draftEl.current
          if (!box) return
          box.focus()
          box.setSelectionRange(next.caret, next.caret)
        })
      } catch (e) {
        reportVoiceError(e, setNote)
      }
    },
    onProblem: setNote,
    maxMs: recordCapMs,
  })
  const micPress = () => {
    if (micGate(voice.state)) return
    voice.toggle()
  }
  // While this sheet is open, the microphone key means THIS microphone.
  const micRef = useRef(micPress)
  micRef.current = micPress
  useEffect(() => registerMicHotkey(() => micRef.current()), [])
  const micRec = voice.state === 'recording'
  const micBusy = voice.state === 'starting' || voice.state === 'uploading'
  const micSecs = Math.floor(voice.elapsedMs / 1000)
  const micClock = `${Math.floor(micSecs / 60)}:${String(micSecs % 60).padStart(2, '0')}`

  const image = shot.data?.image
  const address = webAddress(shot.data?.url)
  const takenAt = shot.dataUpdatedAt ? new Date(shot.dataUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''

  // The agent's face — a door to its session where there is one.
  const face = onGoToAgent ? (
    <button
      type="button"
      className="tab-shot-face"
      onClick={() => onGoToAgent({ session, hash: told.agent.hash, device: told.agent.device })}
      aria-label={`Open ${session}`}
      title={`Open ${session}`}
    >
      <PixelPet name={session} seed={told.agent.hash} size={30} />
    </button>
  ) : (
    <span className="tab-shot-face" aria-hidden="true">
      <PixelPet name={session} seed={told.agent.hash} size={30} />
    </span>
  )
  const compose = (
    <div className="nt-compose tab-shot-compose">
      <textarea
        ref={draftEl}
        className="nt-draft"
        rows={2}
        value={draft}
        aria-label={`Message to ${session}`}
        placeholder={`Tell ${session} what to do in this tab…`}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends (Cmd/Ctrl+Enter too), Shift+Enter is a new line; an Enter that is
          // choosing a word in an input method is that, not a send (ui/enterSends.ts).
          if (enterSends(e)) {
            keepEnter(e)
            send()
          }
        }}
      />
      <div className="nt-compose-btns">
        {note && (
          <span className="tab-shot-note" role="status">
            {note}
          </span>
        )}
        <button
          type="button"
          className={`nt-cbtn mic${micRec ? ' rec' : micBusy ? ' busy' : ''}`}
          disabled={micBusy}
          onClick={micPress}
          aria-label={micRec ? 'Stop recording' : 'Record voice input'}
          aria-pressed={micRec}
          title={micRec ? `Stop recording and insert the words (${MIC_HOTKEY_LABEL})` : voice.state === 'uploading' ? 'Transcribing…' : `Record, then insert the words (${MIC_HOTKEY_LABEL})`}
        >
          {micRec ? <span className="mic-clock">{micClock}</span> : <Icon name="mic" size={18} />}
        </button>
        <button
          type="button"
          className="nt-save"
          disabled={!draft.trim() || say.isPending}
          onClick={send}
          aria-label={`Send to ${session}`}
          title={`Send to ${session} (Enter)`}
        >
          <Icon name={say.isPending ? 'sync' : 'send'} size={18} />
        </button>
      </div>
    </div>
  )
  const view = (
    <>
      {strip.length > 1 && (
        <div className="tab-shot-tabs" role="group" aria-label={`Tabs ${session} holds`} ref={stripEl}>
          {strip.map((h) => {
            const on = isThis(h)
            const label = h.told.title || `Tab ${h.told.tab}`
            return (
              <button
                key={`${h.browser.browser_id}:${h.told.tab}`}
                type="button"
                className={`tab-shot-tab${on ? ' on' : ''}`}
                aria-current={on ? 'true' : undefined}
                title={`${label} · ${h.browser.name}`}
                onClick={() => moveTo(h)}
              >
                <span className={`art-dot ${h.told.paused ? 'art-wait' : 'art-ok'}`} aria-hidden="true" />
                <span className="tab-shot-tab-name">{label}</span>
              </button>
            )
          })}
        </div>
      )}
      <div className={`tab-shot-view${big ? ' big' : ''}`}>
        {!canSee ? (
          <p className="more-note">
            {browser.shots === false
              ? 'Update the Avengents extension in this browser to see its tabs here'
              : 'Pictures of tabs are not available from the Avengents service yet'}
          </p>
        ) : image ? (
          /* A phone draws the picture a quarter of its size, and the app does not pinch-zoom:
             a tap shows it at full size, to scroll around in; another fits it again. */
          <button
            type="button"
            className="tab-shot-zoom"
            aria-pressed={big}
            aria-label={big ? 'Fit the picture to the screen' : 'Show the picture at full size'}
            onClick={() => setBig((b) => !b)}
          >
            <img className="tab-shot-img" src={`data:${image.mime};base64,${image.base64}`} alt={`What ${name} shows`} />
          </button>
        ) : shot.isError ? (
          <p className="more-note" role="alert">
            {said(shot.error)}
          </p>
        ) : (
          <p className="more-note">Taking a picture of the tab…</p>
        )}
      </div>
      {canSee && (
        <div className="tab-shot-bar">
          {/* The page itself, on this device: a phone hands it to the system, which opens the
              site's own app when there is one; the Mac app to the person's browser. */}
          {address && (
            <a className="tab-open-page" href={address} target="_blank" rel="noopener noreferrer">
              <Icon name="open_in_new" size={18} />
              Open page
            </a>
          )}
          {/* A new picture that could not be taken says why, and that this one is older. */}
          {shot.isError && image && !shot.isFetching ? (
            <span className="tab-shot-when tab-shot-failed" role="alert">
              {`No new picture: ${said(shot.error)} (this one is from ${takenAt})`}
            </span>
          ) : (
            <span className="tab-shot-when">{shot.isFetching ? 'Taking a picture…' : takenAt && `Taken ${takenAt}`}</span>
          )}
          <button type="button" className="tab-act" onClick={takeAgain} disabled={shot.isFetching} aria-label="Take the picture again" title="Take the picture again">
            <Icon name="refresh" size={18} />
          </button>
        </div>
      )}
    </>
  )
  const sub = (
    <>
      {session}
      {told.agent.machine && ` · ${told.agent.machine}`}
      {told.paused && ' · Paused'}
    </>
  )

  if (page)
    return (
      <StagePage {...page} title={name} sub={sub} glyph={face} backLabel="Back to Chrome tabs" footer={compose} className="tab-shot-page">
        {view}
      </StagePage>
    )

  return (
    <Sheet
      title={name}
      className="sheet-tall tab-shot"
      onClose={onClose}
      head={
        <span className="tab-shot-head">
          {face}
          <span className="tab-shot-hd">
            <span className="sheet-title">{name}</span>
            <span className="tab-shot-sub">{sub}</span>
          </span>
        </span>
      }
      actions={compose}
    >
      {view}
    </Sheet>
  )
}
